import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  CreditCard,
  Package,
  ShoppingCart,
  Users,
  AlertCircle,
  Factory,
  Wallet,
  Percent,
} from "lucide-react";
import { useDashboardOverview } from "../api/dashboard";
import { useSalesReport } from "../api/reports";
import { useLowStock } from "../api/inventory";
import { PageHeader } from "../components/ui/PageHeader";
import { StatCard } from "../components/ui/StatCard";
import { LoadingState, ErrorState } from "../components/ui/States";
import { formatCurrency, formatNumber, humanizeEnum, toNumber } from "../utils/helpers";

const STATUS_COLORS: Record<string, string> = {
  PENDING: "#f59e0b",
  CONFIRMED: "#3b82f6",
  PACKED: "#8b5cf6",
  DISPATCHED: "#06b6d4",
  DELIVERED: "#10b981",
  CANCELLED: "#ef4444",
};

const chartTooltip = {
  contentStyle: { backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: 8 },
  labelStyle: { color: "#e2e8f0" },
};

export const Dashboard: React.FC = () => {
  const overview = useDashboardOverview();
  const sales = useSalesReport();
  const lowStock = useLowStock();

  if (overview.isLoading) return <LoadingState label="Loading dashboard…" />;
  if (overview.isError)
    return <ErrorState error={overview.error} onRetry={() => overview.refetch()} />;

  const data = overview.data!;
  const statusData = data.orders.byStatus.map((s) => ({
    name: humanizeEnum(s.status),
    value: s.count,
    fill: STATUS_COLORS[s.status] ?? "#64748b",
  }));

  const topProducts = (sales.data?.topProducts ?? []).map((p) => ({
    name: p.product.name ?? p.product.sku ?? "Unknown",
    revenue: toNumber(p.revenue),
  }));

  const lowStockItems = lowStock.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="Live overview of orders, production, stock and receivables."
      />

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<ShoppingCart size={22} className="text-blue-400" />}
          label="Today's Orders"
          value={data.orders.today.count}
          hint={formatCurrency(data.orders.today.amount)}
          accent="border-blue-500"
        />
        <StatCard
          icon={<CreditCard size={22} className="text-amber-400" />}
          label="Pending Orders"
          value={data.orders.pending}
          hint="Awaiting confirmation"
          accent="border-amber-500"
        />
        <StatCard
          icon={<Wallet size={22} className="text-red-400" />}
          label="Receivables"
          value={formatCurrency(data.receivables.totalReceivable)}
          hint={`${data.receivables.clientCount} active clients`}
          accent="border-red-500"
        />
        <StatCard
          icon={<Package size={22} className="text-emerald-400" />}
          label="Stock Value"
          value={formatCurrency(data.stock.totalValue)}
          hint={`${data.stock.productCount} products`}
          accent="border-emerald-500"
        />
      </div>

      {/* Low stock alert */}
      {lowStockItems.length > 0 && (
        <div className="card flex items-start gap-4 border-l-4 border-red-500 bg-red-900/10 p-4">
          <AlertCircle className="mt-0.5 shrink-0 text-red-400" size={20} />
          <div>
            <h3 className="mb-1 font-semibold text-red-300">Low Stock Alert</h3>
            <p className="text-sm text-red-200/80">
              {lowStockItems.length} product(s) at or below threshold:{" "}
              {lowStockItems
                .slice(0, 6)
                .map((p) => p.name)
                .join(", ")}
              {lowStockItems.length > 6 ? "…" : ""}
            </p>
          </div>
        </div>
      )}

      {/* Production + secondary stats */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Factory size={22} className="text-violet-400" />}
          label="Runs This Month"
          value={data.production.completedThisMonth}
          accent="border-violet-500"
        />
        <StatCard
          icon={<Percent size={22} className="text-cyan-400" />}
          label="Avg. Yield"
          value={`${formatNumber(data.production.averageYield, 1)}%`}
          accent="border-cyan-500"
        />
        <StatCard
          icon={<Package size={22} className="text-emerald-400" />}
          label="Output (kg)"
          value={formatNumber(data.production.totalOutputKg)}
          hint={`Wastage ${formatNumber(data.production.totalWastageKg)} kg`}
          accent="border-emerald-500"
        />
        <StatCard
          icon={<Users size={22} className="text-amber-400" />}
          label="Salaries Outstanding"
          value={formatCurrency(data.salaries.totalRemaining)}
          hint={data.salaries.periodMonth}
          accent="border-amber-500"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <ShoppingCart size={20} className="text-purple-400" />
            Orders by Status
          </h2>
          {statusData.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-400">No orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name} (${value})`}
                  outerRadius={90}
                  dataKey="value"
                >
                  {statusData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip {...chartTooltip} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <Package size={20} className="text-blue-400" />
            Top Products (last 30 days)
          </h2>
          {topProducts.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-400">
              No sales recorded in this period.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topProducts} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis dataKey="name" type="category" stroke="#94a3b8" width={140} />
                <Tooltip
                  {...chartTooltip}
                  formatter={(v: number) => formatCurrency(v)}
                />
                <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};
